/*
 * business-card/ink.js — colour, as ink rather than as light.
 *
 * THE COMPLAINT THIS EXISTS TO KILL
 * ─────────────────────────────────
 * "На экране был яркий синий, а с печати пришло что-то тусклое." It is the
 * single most common print-shop complaint, and every online card maker causes
 * it the same way: the user picks an sRGB colour, sees an sRGB preview, and the
 * press prints CMYK. The gap between those two is not a bug in the press. A
 * monitor emits light and can make #0080FF; four inks on paper cannot, and no
 * setting anywhere recovers it.
 *
 * So the fix is not a better conversion at export time. It is to STOP LYING ON
 * SCREEN. This module makes CMYK the authoritative colour and renders the
 * preview THROUGH an ink model, so the dull blue is dull in the browser, before
 * any money is spent. Being disappointed by a preview is free.
 *
 * WHY NEUGEBAUER AND NOT `r = (1-c)(1-k)`
 * ───────────────────────────────────────
 * The naive formula assumes perfect inks: cyan that reflects zero red, magenta
 * that reflects zero green. Real process inks are nothing like that — cyan is
 * roughly (0,158,224), not (0,255,255) — and the error is not uniform. Naive
 * says C+M = (0,0,255), a vivid blue. On paper C+M is a dark violet around
 * (46,20,130). A preview built on the naive formula therefore promises exactly
 * the colours that disappoint hardest.
 *
 * The Demichel/Neugebauer model instead treats a halftone as an area mix of the
 * eight things that can be under the microscope — bare paper, each single ink,
 * each two-ink overprint, and the three-ink overprint — weighted by the
 * probability of each overlap. It is the standard first-order model of offset
 * printing, it needs only the eight measured solids, and it costs a dozen
 * multiplications.
 *
 * The solids below are the customary coated-offset process primaries (the
 * FOGRA39/ISO 12647-2 family). They are an approximation of a press, not a
 * calibration of one: no browser can know the shop, the stock or the profile.
 * What they buy is the difference between "roughly right" and "confidently
 * wrong", and that is the whole distance the complaint lives in.
 *
 * WHY THE EXPORT CARRIES NO ICC PROFILE
 * ─────────────────────────────────────
 * Because RU shops explicitly ask for none ("макет не должен содержать
 * ICC-профилей"): they want DeviceCMYK numbers they can send straight to plate.
 * That makes the CMYK values themselves the contract, which is another reason
 * they must be what the user actually chose — there is no profile downstream to
 * reinterpret them.
 *
 * Pure module: no DOM, no browser API.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CardInk = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* ── the eight Neugebauer primaries, coated offset ───────────────────────
     * Order is the binary one: index bit0=C, bit1=M, bit2=Y. Keeping it
     * positional lets the Demichel weights be computed in one loop instead of
     * eight hand-written products, which is where a transposition would hide.
     */
    var PRIMARIES = [
        [255, 255, 255],   // 000  bare paper
        [  0, 158, 224],   // 001  C
        [230,   0, 126],   // 010  M
        [ 46,  20, 130],   // 011  C+M  — violet-blue, NOT (0,0,255)
        [255, 237,   0],   // 100  Y
        [  0, 152,  84],   // 101  C+Y  — green
        [227,  30,  36],   // 110  M+Y  — red
        [ 40,  34,  33],   // 111  C+M+Y — composite black, warm and open
    ];

    /* Solid K. Process black is not (0,0,0): it reflects a little, and a card
     * printed in 100 K next to one in rich black is visibly lighter. Showing
     * that difference on screen is the point of having a K primary at all. */
    var K_SOLID = [29, 27, 27];

    /* Dot gain. Ink spreads into the paper, so a 50 % tint measures nearer 60 %.
     * Ignoring it makes every preview lighter than the print — mid-tones most
     * of all, which is where a "light grey" line becomes a "grey" line. 0.16 is
     * the usual coated-stock figure at the midtone, applied as a Yule-Nielsen
     * style curve so the endpoints stay pinned at 0 and 1. */
    var DOT_GAIN = 0.16;

    function gain(t) {
        if (t <= 0) return 0;
        if (t >= 1) return 1;
        /* A single-parameter curve that is 0 at 0, 1 at 1, and +DOT_GAIN at 0.5 */
        return t + DOT_GAIN * Math.sin(Math.PI * t) * (1 - t) * 2;
    }

    function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
    function clamp255(v) { return v < 0 ? 0 : (v > 255 ? 255 : v); }

    /* ── CMYK → sRGB, the honest direction ──────────────────────────────────
     * cmyk values are 0..1.
     */
    function cmykToRgb(c, m, y, k) {
        var o = forwardRgb(c, m, y, k, [0, 0, 0]);
        return [Math.round(clamp255(o[0])), Math.round(clamp255(o[1])), Math.round(clamp255(o[2]))];
    }

    /* The forward model unrounded, into `out` — what the photo separation below solves
     * against (rounding to whole RGB steps would give its Jacobian flat spots). */
    function forwardRgb(c, m, y, k, out) {
        var cc = gain(clamp01(c)), mm = gain(clamp01(m)), yy = gain(clamp01(y)), kk = gain(clamp01(k));
        var w = [
            (1 - cc) * (1 - mm) * (1 - yy),
            cc * (1 - mm) * (1 - yy),
            (1 - cc) * mm * (1 - yy),
            cc * mm * (1 - yy),
            (1 - cc) * (1 - mm) * yy,
            cc * (1 - mm) * yy,
            (1 - cc) * mm * yy,
            cc * mm * yy,
        ];
        var r = 0, g = 0, b = 0;
        for (var i = 0; i < 8; i++) {
            r += w[i] * PRIMARIES[i][0];
            g += w[i] * PRIMARIES[i][1];
            b += w[i] * PRIMARIES[i][2];
        }
        /* K prints ON TOP of the CMY build. Where a K dot lands, the light has
         * to pass the black ink AND the colour underneath, so that area is the
         * PRODUCT of the two — not the black solid on its own. Where no K dot
         * lands, the colour shows through untouched. The two areas then mix by
         * the K coverage, exactly as Demichel does for the other three plates.
         *
         * Writing this as a blend toward K_SOLID — `r*(1-kk) + K_SOLID*kk` —
         * looks equivalent and is not: at kk = 1 it discards the CMY build
         * entirely, so rich black (CMY under the K) comes out byte-identical to
         * flat 100 K. That is the one comparison the whole rich-black rule
         * depends on, and the blend form silently answers "no difference".
         * Measured here: flat #1d1b1b vs rich #0c0d0d. */
        var kt = (1 - kk) + kk * (K_SOLID[0] / 255);
        var gt = (1 - kk) + kk * (K_SOLID[1] / 255);
        var bt = (1 - kk) + kk * (K_SOLID[2] / 255);
        r *= kt; g *= gt; b *= bt;
        out[0] = r; out[1] = g; out[2] = b;
        return out;
    }

    /* ── sRGB → CMYK ────────────────────────────────────────────────────────
     * Inverting an eight-primary model has no closed form, so this searches:
     * a naive GCR seed, then coordinate descent against the forward model.
     *
     * The objective is not pure colour distance. Two CMYK builds can hit the
     * same colour while one lays down 260 % ink and the other 190 %, and the
     * lighter one is strictly better — it dries, it registers, it does not
     * offset onto the next card. So total area coverage is a tie-breaker with
     * a small weight: enough to prefer the sane build, never enough to accept
     * a visibly wrong colour.
     */
    function rgbToCmyk(r, g, b, opt) {
        opt = opt || {};
        var maxInk = (typeof opt.maxInk === 'number') ? opt.maxInk : 3.0;   // 300 %
        var R = clamp01(r / 255), G = clamp01(g / 255), B = clamp01(b / 255);

        /* ── greys are K-only, always ────────────────────────────────────────
         * Not an optimisation — a press rule. A neutral built from CMY needs
         * three plates to register perfectly to STAY neutral, and they never
         * do; the grey drifts warm or green across the sheet and the drift is
         * visible precisely because the eye has a reference for neutral. One
         * plate cannot misregister against itself.
         *
         * This is also what stops the search doing something absurd on pure
         * black. Left to itself it finds C98 M100 Y100 K100 — a better colour
         * match than flat black, 398 % ink, and a sheet that never dries. */
        if (Math.max(R, G, B) - Math.min(R, G, B) <= 8 / 255) {
            var bestK = 0, bestKe = Infinity;
            for (var t = 0; t <= 1000; t++) {
                var kk = t / 1000;
                var e = dist2(cmykToRgb(0, 0, 0, kk), [r, g, b]);
                if (e < bestKe) { bestKe = e; bestK = kk; }
            }
            return { c: 0, m: 0, y: 0, k: round3(bestK) };
        }

        /* Naive maximum-GCR seed: all the neutral content into K. It is a poor
         * colour match but an excellent starting point, because it is already
         * on the low-ink side of the solution space. */
        var k0 = 1 - Math.max(R, G, B);
        var seed;
        if (k0 >= 0.999) seed = [0, 0, 0, 1];
        else seed = [
            (1 - R - k0) / (1 - k0),
            (1 - G - k0) / (1 - k0),
            (1 - B - k0) / (1 - k0),
            k0,
        ];
        for (var i = 0; i < 4; i++) seed[i] = clamp01(seed[i]);

        var best = seed.slice();
        var bestErr = cost(best, r, g, b, maxInk);

        /* Coordinate descent with a shrinking step. Four channels, ~7 steps,
         * two passes each — a few hundred forward evaluations, which is
         * microseconds and runs once per colour the user picks. */
        var step = 0.5;
        for (var pass = 0; pass < 22; pass++) {
            var moved = false;
            for (var ch = 0; ch < 4; ch++) {
                for (var dir = -1; dir <= 1; dir += 2) {
                    var trial = best.slice();
                    trial[ch] = clamp01(trial[ch] + dir * step);
                    var e = cost(trial, r, g, b, maxInk);
                    if (e < bestErr - 1e-9) { best = trial; bestErr = e; moved = true; }
                }
            }
            if (!moved) step *= 0.5;
            if (step < 0.002) break;
        }
        return { c: round3(best[0]), m: round3(best[1]), y: round3(best[2]), k: round3(best[3]) };
    }

    function cost(v, r, g, b, maxInk) {
        var ink = (v[0] + v[1] + v[2] + v[3]);
        /* The coverage ceiling is a HARD constraint, not a preference. A build
         * over it is not a slightly worse colour, it is a sheet that does not
         * dry and sets off onto the next card — no closeness of match buys that
         * back. Expressing it as a penalty instead lets a good colour match
         * outbid it every time, which is exactly what happened here before. */
        if (ink > maxInk) return Infinity;
        var got = cmykToRgb(v[0], v[1], v[2], v[3]);
        var d = dist2(got, [r, g, b]);
        /* Below the ceiling, less ink is still better — cheaper, drier, and it
         * registers. Small weight: it only ever breaks ties. */
        return d + ink * 6;
    }

    /* Squared distance in a cheap perceptual weighting (the "low-cost
     * approximation" weights). Full CIEDE2000 would be more correct and is not
     * worth the code here: the search only needs a monotone ordering. */
    function dist2(a, b) {
        var rm = (a[0] + b[0]) / 2;
        var dr = a[0] - b[0], dg = a[1] - b[1], db = a[2] - b[2];
        return (2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db;
    }

    function round3(v) { return Math.round(clamp01(v) * 1000) / 1000; }

    /* ── gamut ──────────────────────────────────────────────────────────────
     * "Can the press make this colour?" answered by round-tripping: convert to
     * the best CMYK we can find, convert back, and measure how far it landed.
     * `deltaish` is a rough perceptual distance in 0..100-ish units, so the UI
     * can say "slightly duller" and "this will not survive" differently.
     */
    function gamutCheck(r, g, b) {
        var cmyk = rgbToCmyk(r, g, b);
        var back = cmykToRgb(cmyk.c, cmyk.m, cmyk.y, cmyk.k);
        var d = Math.sqrt(dist2(back, [r, g, b])) / 30;
        return {
            cmyk: cmyk,
            printed: back,
            shift: Math.round(d * 10) / 10,
            /* Thresholds set by eye against the printed patches this model
             * describes: below 2 nobody notices, above 6 the user will call it
             * "a different colour". */
            level: d < 2 ? 'ok' : (d < 6 ? 'shift' : 'out'),
        };
    }

    /* ── ink coverage ───────────────────────────────────────────────────────
     * Total Area Coverage: the sum of the four channels as a percentage. Above
     * ~300 % on coated stock the sheet stops drying between passes and sets off
     * onto the back of the next one in the stack. It is the one colour fault a
     * shop cannot fix for you at the RIP.
     */
    function tac(cmyk) {
        return Math.round((cmyk.c + cmyk.m + cmyk.y + cmyk.k) * 100);
    }

    /* ── black, of which there are two ──────────────────────────────────────
     * FLAT black (0/0/0/100) is the only black for small text: one plate, so
     * misregistration cannot blur it. It looks slightly washed over a large
     * area, which nobody minds on 8 pt type.
     * RICH black adds CMY under the K so a big solid reads as deep black. Use
     * it for backgrounds and display type; use it on a phone number and the
     * number gets a coloured ghost 0.1 mm to one side.
     * The numbers below are the conservative "cool rich black" build at 240 %,
     * well under the coverage ceiling.
     */
    var FLAT_BLACK = { c: 0, m: 0, y: 0, k: 1 };
    var RICH_BLACK = { c: 0.6, m: 0.4, y: 0.4, k: 1 };

    function isRichBlack(cmyk) {
        return cmyk.k > 0.85 && (cmyk.c + cmyk.m + cmyk.y) > 0.25;
    }
    function isBlackish(cmyk) {
        return cmyk.k > 0.85;
    }

    /* ── hex plumbing ───────────────────────────────────────────────────────*/
    function hexToRgb(hex) {
        var h = String(hex || '').replace('#', '').trim();
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
        var n = parseInt(h, 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    function rgbToHex(rgb) {
        return '#' + rgb.map(function (v) {
            var s = Math.round(clamp255(v)).toString(16);
            return s.length < 2 ? '0' + s : s;
        }).join('');
    }

    /* The colour every other module passes around. CMYK is authoritative; `hex`
     * is the SIMULATED print appearance and exists only so canvas and CSS have
     * something to consume. Storing the user's original sRGB here instead is
     * the mistake this whole file is about. */
    function fromCmyk(c, m, y, k) {
        var cmyk = { c: clamp01(c), m: clamp01(m), y: clamp01(y), k: clamp01(k) };
        cmyk.hex = rgbToHex(cmykToRgb(cmyk.c, cmyk.m, cmyk.y, cmyk.k));
        cmyk.tac = tac(cmyk);
        return cmyk;
    }
    function fromHex(hex) {
        var rgb = hexToRgb(hex);
        if (!rgb) return fromCmyk(0, 0, 0, 1);
        var q = rgbToCmyk(rgb[0], rgb[1], rgb[2]);
        return fromCmyk(q.c, q.m, q.y, q.k);
    }

    /* ── palettes that survive the press ────────────────────────────────────
     * Curated in CMYK, not in hex, and every entry is under the coverage
     * ceiling by construction. This is the quiet half of the fix: a user who
     * picks from here never meets the disappointment at all, because there is
     * no vivid screen colour on offer that the press cannot hold.
     *
     * Each palette is [ink, accent, muted, paper] — the four roles the layouts
     * ask for. Keeping the roles fixed is what lets a preset be re-skinned in
     * one click without any layout knowing which palette it is wearing.
     */
    var PALETTES = [
        ['graphite',  [0,0,0,1],              [0,0,0,0.55],           [0,0,0,0.22],           [0,0,0,0]],
        ['ink-gold',  [0.75,0.68,0.55,0.85],  [0.10,0.28,0.85,0.05],  [0.06,0.10,0.22,0.03],  [0,0,0.03,0.01]],
        ['navy',      [0.95,0.72,0.30,0.35],  [0.72,0.28,0,0],        [0.16,0.06,0,0.02],     [0,0,0,0]],
        ['bordeaux',  [0.30,1,0.72,0.30],     [0.10,0.85,0.60,0.05],  [0.05,0.20,0.14,0.02],  [0,0.02,0.02,0]],
        ['forest',    [0.85,0.35,0.75,0.45],  [0.62,0.08,0.72,0],     [0.14,0.02,0.16,0.02],  [0.02,0,0.03,0]],
        ['terracotta',[0.20,0.72,0.75,0.35],  [0.05,0.55,0.65,0],     [0.03,0.14,0.18,0.02],  [0.01,0.03,0.06,0]],
        ['slate',     [0.55,0.35,0.25,0.75],  [0.45,0.20,0.10,0.15],  [0.12,0.06,0.03,0.06],  [0.01,0,0,0.01]],
        ['plum',      [0.65,0.95,0.25,0.30],  [0.45,0.75,0,0],        [0.10,0.16,0,0.02],     [0.01,0.01,0,0]],
        ['teal',      [0.90,0.35,0.40,0.30],  [0.75,0.12,0.30,0],     [0.16,0.02,0.06,0.02],  [0,0,0,0]],
        ['mono-warm', [0.20,0.25,0.35,0.95],  [0.12,0.18,0.30,0.55],  [0.04,0.06,0.10,0.18],  [0,0.01,0.03,0]],
    ];

    function palettes() {
        return PALETTES.map(function (p) {
            return {
                id: p[0],
                ink:    fromCmyk(p[1][0], p[1][1], p[1][2], p[1][3]),
                accent: fromCmyk(p[2][0], p[2][1], p[2][2], p[2][3]),
                muted:  fromCmyk(p[3][0], p[3][1], p[3][2], p[3][3]),
                paper:  fromCmyk(p[4][0], p[4][1], p[4][2], p[4][3]),
            };
        });
    }

    function paletteById(id) {
        var list = palettes();
        for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
        return list[0];
    }

    /* ── a palette built around one brand colour ─────────────────────────────
     * A company that already owns a colour cannot use a curated set, and
     * "pick the nearest of our ten" is not an answer to "this is our blue".
     *
     * The other three roles are DERIVED rather than asked for, because the
     * roles are a relationship, not four independent choices: a user who picks
     * four colours by hand picks four that fight. The ink is the same colour
     * driven much darker, the muted is the same colour as a pale tint, and the
     * paper stays paper.
     *
     * ⚠️ Derivation happens in CMYK, not by darkening the sRGB. Multiplying the
     * screen colour toward black and re-converting drifts the hue — the whole
     * reason this module exists — whereas adding K to an existing build is what
     * a press actually does to make a darker version of an ink.
     */
    function customPalette(hex) {
        var accent = fromHex(hex);
        var ink = fromCmyk(
            Math.min(1, accent.c * 1.05 + 0.10),
            Math.min(1, accent.m * 1.05 + 0.06),
            Math.min(1, accent.y * 1.05 + 0.06),
            Math.min(0.92, accent.k + 0.45));
        var muted = fromCmyk(accent.c * 0.26, accent.m * 0.26, accent.y * 0.26, accent.k * 0.18);
        var paper = fromCmyk(0, 0, 0, 0);
        /* If the brand colour is already very dark, the derived ink lands on
         * top of it and the card loses its contrast pair. Fall back to a
         * near-neutral ink, which is what a designer does with a dark brand. */
        if (contrast(ink, accent) < 1.7) ink = fromCmyk(0, 0, 0, 1);
        return { id: 'custom', ink: ink, accent: accent, muted: muted, paper: paper };
    }

    /* ── contrast, measured on the printed colours ──────────────────────────
     * WCAG contrast is defined for emitted light, but the arithmetic answers a
     * question that matters on paper too: is this legible? Feeding it the
     * SIMULATED print colours rather than the picked sRGB is the difference
     * between grading the card and grading the monitor.
     */
    function luminance(rgb) {
        var a = rgb.map(function (v) {
            v /= 255;
            return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
        });
        return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
    }
    function contrast(cmykA, cmykB) {
        var la = luminance(cmykToRgb(cmykA.c, cmykA.m, cmykA.y, cmykA.k));
        var lb = luminance(cmykToRgb(cmykB.c, cmykB.m, cmykB.y, cmykB.k));
        var hi = Math.max(la, lb), lo = Math.min(la, lb);
        return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
    }


    /* ── whole bitmaps, not single colours ───────────────────────────────────
     *
     * Both the preview and the exporter have to put an uploaded logo through
     * this model, and they MUST agree: the point of showing printed colour is
     * lost the moment the two disagree about what printed colour is. So the
     * conversion lives here once and both call it.
     *
     * ⚠️ `rgbToCmyk` is a SEARCH, priced per DISTINCT colour, and real images
     * differ by three orders of magnitude. Measured 2026-08-23 on 900x300:
     *
     *     flat two-colour mark    199 distinct      17 ms
     *     photographic gradient   59 696 distinct   1963 ms   ← two seconds
     *
     * A logo is nearly always the first case, so it gets the exact conversion.
     * Past the threshold the palette is bucketed to 5 bits per channel, which
     * took the same photograph to 898 buckets and 39 ms — the rounding is under
     * 2 % per channel, far below the shift the conversion itself applies. */
    var EXACT_MAX_COLOURS = 1500;

    function paletteFor(data, n) {
        var seen = new Set(), i, o;
        for (i = 0; i < n && seen.size <= EXACT_MAX_COLOURS; i++) {
            o = i * 4;
            seen.add((data[o] << 16) | (data[o + 1] << 8) | data[o + 2]);
        }
        var exact = seen.size <= EXACT_MAX_COLOURS;
        var memo = new Map();
        return {
            exact: exact,
            colours: seen.size,
            at: function (r, g, b) {
                var key = exact ? ((r << 16) | (g << 8) | b)
                                : (((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3));
                var c = memo.get(key);
                if (c === undefined) {
                    c = exact ? rgbToCmyk(r, g, b)
                              : rgbToCmyk((r & 248) | 4, (g & 248) | 4, (b & 248) | 4);
                    memo.set(key, c);
                }
                return c;
            },
        };
    }

    /** RGBA bytes → DeviceCMYK samples (+ an 8-bit alpha plane when needed). */
    function cmykBitmap(data, w, h) {
        var n = w * h, pal = paletteFor(data, n);
        var samples = new Uint8Array(n * 4), alpha = null;
        for (var i = 0; i < n; i++) {
            var q = i * 4;
            var a = data[q + 3];
            if (a < 255) { if (!alpha) { alpha = new Uint8Array(n); alpha.fill(255); } alpha[i] = a; }
            var c = pal.at(data[q], data[q + 1], data[q + 2]);
            samples[q]     = Math.round(clamp01(c.c) * 255);
            samples[q + 1] = Math.round(clamp01(c.m) * 255);
            samples[q + 2] = Math.round(clamp01(c.y) * 255);
            samples[q + 3] = Math.round(clamp01(c.k) * 255);
        }
        return { samples: samples, alpha: alpha, exact: pal.exact, colours: pal.colours };
    }

    /* ── photographs: a smooth separation ───────────────────────────────────
     * rgbToCmyk above is right for a LOGO or a picked colour — greys K-only, the least ink
     * that hits the colour — and wrong for a photograph. It answers each colour on its own,
     * and in the shadows many CMYK builds hit nearly the same colour, so two neighbouring
     * pixels of a navy jumper can come out C 0 % and C 100 % (measured 30.10.2026 on a
     * photo: the cyan plate was a mosaic of 8×8 blocks). On paper that is mottle: plates
     * never register perfectly, and the jumper prints blotchy.
     *
     * Photos get what a press separation does: black is fixed first by a GCR curve on the
     * darkness of the colour (none in the light third, then a smooth rise to 95 %), and C, M,
     * Y are SOLVED for that K against the same forward model (damped Gauss–Newton, three
     * unknowns, three equations — one smooth answer). Total ink is capped at 300 % by
     * pulling C, M, Y down under the black. The answer is tabulated once on a 33³ grid and
     * read by trilinear interpolation, as an ICC CLUT would be.
     */
    var GCR_START = 0.3, GCR_MAX = 0.95, PHOTO_TAC = 3.0;
    function photoBlack(r, g, b) {
        var k0 = 1 - Math.max(r, g, b) / 255;
        if (k0 <= GCR_START) return 0;
        var t = (k0 - GCR_START) / (1 - GCR_START);
        return GCR_MAX * (t * t * (3 - 2 * t) * 0.5 + t * 0.5);
    }
    function solve3(A, v) {
        var a = A[0][0], b = A[0][1], c = A[0][2], d = A[1][0], e = A[1][1], f = A[1][2], g = A[2][0], h = A[2][1], i = A[2][2];
        var det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
        if (Math.abs(det) < 1e-12) return null;
        return [
            ((e * i - f * h) * v[0] - (b * i - c * h) * v[1] + (b * f - c * e) * v[2]) / det,
            (-(d * i - f * g) * v[0] + (a * i - c * g) * v[1] - (a * f - c * d) * v[2]) / det,
            ((d * h - e * g) * v[0] - (a * h - b * g) * v[1] + (a * e - b * d) * v[2]) / det,
        ];
    }
    /* C, M, Y for a GIVEN black: damped Gauss–Newton against the forward model; returns the
     * plates and how far (RGB units) they land from the target. */
    function solveCmyFor(r, g, b, k, x0) {
        var kk = Math.min(0.99, k);
        var x = x0 ? x0.slice() : [1 - r / 255, 1 - g / 255, 1 - b / 255].map(function (v) { return clamp01((v - kk) / (1 - kk)); });
        var o0 = [0, 0, 0], o1 = [0, 0, 0], t = [r, g, b], e = [0, 0, 0];
        for (var it = 0; it < 16; it++) {
            forwardRgb(x[0], x[1], x[2], k, o0);
            e = [o0[0] - t[0], o0[1] - t[1], o0[2] - t[2]];
            if (e[0] * e[0] + e[1] * e[1] + e[2] * e[2] < 0.05) break;
            var J = [[0, 0, 0], [0, 0, 0], [0, 0, 0]], hs = 1e-3;
            for (var j = 0; j < 3; j++) {
                var xs = x.slice();
                xs[j] = xs[j] + hs > 1 ? xs[j] - hs : xs[j] + hs;
                var s = xs[j] - x[j];
                forwardRgb(xs[0], xs[1], xs[2], k, o1);
                for (var q = 0; q < 3; q++) J[q][j] = (o1[q] - o0[q]) / s;
            }
            // damped normal equations: the damping keeps out-of-gamut colours from swinging
            var A = [[0, 0, 0], [0, 0, 0], [0, 0, 0]], v = [0, 0, 0];
            for (var a = 0; a < 3; a++) {
                for (var bb = 0; bb < 3; bb++) A[a][bb] = J[0][a] * J[0][bb] + J[1][a] * J[1][bb] + J[2][a] * J[2][bb] + (a === bb ? 50 : 0);
                v[a] = -(J[0][a] * e[0] + J[1][a] * e[1] + J[2][a] * e[2]);
            }
            var dx = solve3(A, v);
            if (!dx) break;
            var moved = 0;
            for (var n = 0; n < 3; n++) { var nx = clamp01(x[n] + dx[n]); moved += Math.abs(nx - x[n]); x[n] = nx; }
            if (moved < 1e-5) break;
        }
        forwardRgb(x[0], x[1], x[2], k, o0);
        return { x: x, err: Math.sqrt((o0[0] - r) * (o0[0] - r) + (o0[1] - g) * (o0[1] - g) + (o0[2] - b) * (o0[2] - b)) };
    }
    /* Black is the GCR curve's — unless that much black cannot hit the colour. Dark saturated
     * colours (a violet, a bottle green) need their darkness from C+M or C+Y, not from K, and
     * the curve alone left them up to 21 RGB units off (measured 30.10.2026). Then K is the
     * LARGEST black that still lands within REACH, and outside the press gamut the black with
     * the least error: both move continuously with the colour, so the plates stay smooth. */
    var REACH = 2.5;
    function photoSeparation(r, g, b) {
        var kg = photoBlack(r, g, b);
        var best = solveCmyFor(r, g, b, kg), k = kg;
        if (best.err > REACH && kg > 0) {
            // least-error black on [0, kg] (golden section; the error is unimodal in K here)
            var lo = 0, hi = kg, gr = 0.381966;
            var m1 = lo + gr * (hi - lo), m2 = hi - gr * (hi - lo);
            var f1 = solveCmyFor(r, g, b, m1), f2 = solveCmyFor(r, g, b, m2);
            for (var it = 0; it < 14; it++) {
                if (f1.err <= f2.err) { hi = m2; m2 = m1; f2 = f1; m1 = lo + gr * (hi - lo); f1 = solveCmyFor(r, g, b, m1); }
                else { lo = m1; m1 = m2; f1 = f2; m2 = hi - gr * (hi - lo); f2 = solveCmyFor(r, g, b, m2); }
            }
            var kStar = f1.err <= f2.err ? m1 : m2, eStar = Math.min(f1.err, f2.err);
            if (eStar > REACH) { k = kStar; best = solveCmyFor(r, g, b, k); }
            else {
                // the largest black that still reaches: bisection between kStar (reaches) and kg
                var a = kStar, z = kg, ra = solveCmyFor(r, g, b, kStar);
                for (var bi = 0; bi < 14; bi++) {
                    var mid = (a + z) / 2, rm = solveCmyFor(r, g, b, mid, ra.x);
                    if (rm.err <= REACH) { a = mid; ra = rm; } else z = mid;
                }
                k = a; best = ra;
            }
        }
        var x = best.x, sum = x[0] + x[1] + x[2];
        if (sum + k > PHOTO_TAC && sum > 0) { var f = (PHOTO_TAC - k) / sum; x = x.map(function (v) { return v * f; }); }
        return { c: x[0], m: x[1], y: x[2], k: k };
    }
    var PHOTO_N = 33, photoLut = null;
    function photoTable() {
        if (photoLut) return photoLut;
        var N = PHOTO_N, lut = new Float32Array(N * N * N * 4);
        for (var r = 0; r < N; r++) for (var g = 0; g < N; g++) for (var b = 0; b < N; b++) {
            var c = photoSeparation(r * 255 / (N - 1), g * 255 / (N - 1), b * 255 / (N - 1));
            var o = ((r * N + g) * N + b) * 4;
            lut[o] = c.c * 255; lut[o + 1] = c.m * 255; lut[o + 2] = c.y * 255; lut[o + 3] = c.k * 255;
        }
        photoLut = lut;
        return lut;
    }
    /** RGBA (or RGB with `stride` 3) bytes of a photograph → CMYK samples 0..255, w*h*4.
     *  Transparent pixels are taken over white paper. */
    function photoCmyk(data, w, h, stride) {
        stride = stride || 4;
        var N = PHOTO_N, lut = photoTable(), sc = (N - 1) / 255, n = w * h;
        var out = new Uint8Array(n * 4), N2 = N * N;
        for (var i = 0; i < n; i++) {
            var p = i * stride, r = data[p], g = data[p + 1], b = data[p + 2];
            if (stride === 4 && data[p + 3] < 255) {
                var al = data[p + 3] / 255;
                r = r * al + 255 * (1 - al); g = g * al + 255 * (1 - al); b = b * al + 255 * (1 - al);
            }
            var fr = r * sc, fg = g * sc, fb = b * sc;
            var r0 = fr | 0, g0 = fg | 0, b0 = fb | 0;
            if (r0 > N - 2) r0 = N - 2;
            if (g0 > N - 2) g0 = N - 2;
            if (b0 > N - 2) b0 = N - 2;
            var dr = fr - r0, dg = fg - g0, db = fb - b0;
            var o000 = ((r0 * N + g0) * N + b0) * 4, o001 = o000 + 4, o010 = o000 + N * 4, o011 = o010 + 4;
            var o100 = o000 + N2 * 4, o101 = o100 + 4, o110 = o100 + N * 4, o111 = o110 + 4;
            for (var ch = 0; ch < 4; ch++) {
                var c00 = lut[o000 + ch] + (lut[o001 + ch] - lut[o000 + ch]) * db;
                var c01 = lut[o010 + ch] + (lut[o011 + ch] - lut[o010 + ch]) * db;
                var c10 = lut[o100 + ch] + (lut[o101 + ch] - lut[o100 + ch]) * db;
                var c11 = lut[o110 + ch] + (lut[o111 + ch] - lut[o110 + ch]) * db;
                var c0 = c00 + (c01 - c00) * dg, c1 = c10 + (c11 - c10) * dg;
                var v = c0 + (c1 - c0) * dr;
                out[i * 4 + ch] = v <= 0 ? 0 : (v >= 255 ? 255 : (v + 0.5) | 0);
            }
        }
        return out;
    }
    /** CMYK samples (photoCmyk) → RGBA of how they print, for the preview. */
    function simulateCmyk(cmyk, w, h) {
        var n = w * h, out = new Uint8ClampedArray(n * 4), memo = new Map(), o = [0, 0, 0];
        for (var i = 0; i < n; i++) {
            var q = i * 4;
            // 5 bits per plate: the preview is a picture on a screen, not the plate
            var key = ((cmyk[q] >> 3) << 15) | ((cmyk[q + 1] >> 3) << 10) | ((cmyk[q + 2] >> 3) << 5) | (cmyk[q + 3] >> 3);
            var rgb = memo.get(key);
            if (rgb === undefined) {
                forwardRgb(((cmyk[q] >> 3) * 8 + 4) / 255, ((cmyk[q + 1] >> 3) * 8 + 4) / 255, ((cmyk[q + 2] >> 3) * 8 + 4) / 255, ((cmyk[q + 3] >> 3) * 8 + 4) / 255, o);
                rgb = (Math.round(clamp255(o[0])) << 16) | (Math.round(clamp255(o[1])) << 8) | Math.round(clamp255(o[2]));
                memo.set(key, rgb);
            }
            out[q] = rgb >> 16; out[q + 1] = (rgb >> 8) & 255; out[q + 2] = rgb & 255; out[q + 3] = 255;
        }
        return out;
    }

    /** RGBA bytes → how the same pixels will look once printed, in place.
     *  Also reports the largest perceptual shift, so the UI can say so instead
     *  of quietly changing the person's brand colour. */
    function simulateInPlace(data, w, h) {
        var n = w * h, pal = paletteFor(data, n), worst = 0;
        for (var i = 0; i < n; i++) {
            var q = i * 4;
            if (data[q + 3] < 8) continue;
            var r = data[q], g = data[q + 1], b = data[q + 2];
            var c = pal.at(r, g, b);
            var back = cmykToRgb(c.c, c.m, c.y, c.k);
            var d = Math.sqrt((back[0] - r) * (back[0] - r) * 0.3
                            + (back[1] - g) * (back[1] - g) * 0.59
                            + (back[2] - b) * (back[2] - b) * 0.11);
            if (d > worst) worst = d;
            data[q]     = Math.round(back[0]);
            data[q + 1] = Math.round(back[1]);
            data[q + 2] = Math.round(back[2]);
        }
        return { shift: Math.round(worst), exact: pal.exact, colours: pal.colours };
    }

    return {
        PRIMARIES: PRIMARIES,
        FLAT_BLACK: FLAT_BLACK,
        RICH_BLACK: RICH_BLACK,
        cmykToRgb: cmykToRgb,
        rgbToCmyk: rgbToCmyk,
        gamutCheck: gamutCheck,
        tac: tac,
        isRichBlack: isRichBlack,
        isBlackish: isBlackish,
        hexToRgb: hexToRgb,
        rgbToHex: rgbToHex,
        fromCmyk: fromCmyk,
        fromHex: fromHex,
        palettes: palettes,
        paletteById: paletteById,
        customPalette: customPalette,
        contrast: contrast,
        luminance: luminance,
        cmykBitmap: cmykBitmap,
        simulateInPlace: simulateInPlace,
        photoSeparation: photoSeparation,
        photoCmyk: photoCmyk,
        simulateCmyk: simulateCmyk,
    };
});
